import React, {useEffect, useState} from "react";
import {
    UniButton,
    UniCol,
    UniRow,
    UniForm, IntlMessageValue, IntlMessage, UniSpinner
} from "../../../../@WUM/core/Elements";
import UniChecks from "../../../../@WUM/core/Elements/Form/Elements/Check";
import SunEditorConfig from "../../../ticketsystem/modules/components/SuneditorConfig";
import SunEditor from "suneditor-react";
import {useToast} from "../../../../@WUM/core/WebsocketNotification/ToastProvider/ToastProvider";
import {makeSubmitHandler} from "./save/SubmitHandler";
import {ResponseAutoBreakSettings} from "../../API/response/settingstimetracking";
import {TimetrackingInterface} from "./Interfaces/Timetracking.Interface";

interface SettingsState {
    forceAutoBreakOut: boolean;
    timeReminder: string;
    timeAfterAutoBreakOut: string;
    activateBreakOutWarningPopup?: boolean;
    breakOutWarningPopupContent?: string;
    activateBreakOutWarningMail?: boolean;
    breakOutWarningMailContent?: string;
    breakOutWarningMailTitle?: string;
}

const AutoBreakOutSettings = ({
                                  onChangeUnsaved,
                                  userMode = false,
                                  userId,
                              }: TimetrackingInterface) => {

    const [unsaved, setUnsaved] = useState(false);

    const {showToast} = useToast();

    const markUnsaved = () => {
        if (!unsaved) {
            setUnsaved(true);
            onChangeUnsaved?.(true);
        }
    };

    const Response = ResponseAutoBreakSettings(userId);

    useEffect(() => {
        if (!Response) return;
        const r = (Response as any).data ?? Response;

        setSettings({
            forceAutoBreakOut: !!r.forceAutoBreakOut,
            timeReminder: String(r.timeReminder ?? ""),
            timeAfterAutoBreakOut: String(r.timeAfterAutoBreakOut ?? ""),
            activateBreakOutWarningPopup: !!r.activateBreakOutWarningPopup,
            breakOutWarningPopupContent: r.breakOutWarningPopupContent ?? "",
            activateBreakOutWarningMail: !!r.activateBreakOutWarningMail,
            breakOutWarningMailContent: r.breakOutWarningMailContent ?? "",
            breakOutWarningMailTitle: r.breakOutWarningMailTitle ?? "",
        });


        setUnsaved(false);
    }, [Response]);


    const [validated, setValidated] = useState<boolean>(false);

    const [settings, setSettings] = useState<SettingsState | null>(null);


    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = event.target;

        if (
            name === "forceAutoBreakOut" ||
            name === "activateBreakOutWarningPopup" ||
            name === "activateBreakOutWarningMail"
        ) {
            setSettings(prev => (prev ? {...prev, [name]: checked} : prev));
            markUnsaved();
        }
    };

    const setEditorContent = (fieldName: keyof SettingsState, html: string) => {
        setSettings(prev => (prev ? {...prev, [fieldName]: html as any} : prev)); // <-- null-safe
        markUnsaved();
    };

    const finalRoute: string = userMode && userId
        ? `work/autobreaksettings/save/?id=${userId}`
        : "work/autobreaksettings/save/";

    const handleSubmit = makeSubmitHandler({
        showToast,
        route: finalRoute,

        buildPayload: (form) => ({
            id: (Response as any)?.id,
            forceAutoBreakOut: settings!.forceAutoBreakOut,
            timeReminder: form.timeReminder.value,
            timeAfterAutoBreakOut: (form as any).timeAfterAutoBreakOut.value,
            ...(!userMode && {
                activateBreakOutWarningPopup: settings!.activateBreakOutWarningPopup,
                breakOutWarningPopupContent: settings!.breakOutWarningPopupContent,
                activateBreakOutWarningMail: settings!.activateBreakOutWarningMail,
                breakOutWarningMailContent: settings!.breakOutWarningMailContent,
                breakOutWarningMailTitle: (form as any).breakOutWarningMailTitle.value,
            }),
        }),

        successMessage: "Einstellungen gespeichert.",
        errorMessage: "Speichern fehlgeschlagen.",
        setUnsaved,
        onChangeUnsaved,
        setValidated,
    });

    if (Response === undefined || !settings) return <><UniSpinner/></>;

    return (
        <>
            <UniForm validated={validated} Submit={handleSubmit} className={"p-4 worktime-settings"}>
                <p className={"border border-light-subtle rounded p-2 my-2 mt-0 row"}>
                    {IntlMessage({
                        locale: 'de',
                        messageId: 'time.settings.settings.force-auto-break-out-explanation',
                        Store: ''
                    })}
                </p>
                <UniRow>
                    <UniCol>
                        <UniChecks
                            id="forceAutoBreakOut"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.force-auto-break-out", "")}
                            name="forceAutoBreakOut"
                            checked={settings?.forceAutoBreakOut}
                            class="uni-switch ps-0 my-1"
                            onChange={handleSwitchChange}
                            info={{text: "time.settings.settings.force-auto-break-out.helper"}}
                        />
                    </UniCol>
                    <UniCol>
                        <UniForm.InputGroup class={"mb-md-0  my-1"}>
                            <UniForm.InputGroupText class={""}
                                                    text={IntlMessageValue("", "time.settings.clock-out.warning-trigger-minutes", "")}/>
                            <UniForm.FormControl
                                type="number"
                                name="timeReminder"
                                defaultValue={settings?.timeReminder}
                                required
                                step={0.5}
                            />
                            <UniForm.InputGroupText class={""} text={"min"}/>
                            <UniForm.InputGroupHelpText text={"time.settings.clock-out.warning-trigger-minutes.helper"}
                                                        class={"p-0 pe-1"} Store={""}/>
                        </UniForm.InputGroup>
                    </UniCol>

                    <UniCol>
                        <UniForm.InputGroup class={"mb-3"}>
                            <UniForm.InputGroupText class={""}
                                                    text={IntlMessageValue("", "time.settings.time-after-auto-break-out", "")}/>
                            <UniForm.FormControl
                                type="number"
                                name="timeAfterAutoBreakOut"
                                defaultValue={settings?.timeAfterAutoBreakOut}
                                required
                                step={0.25}
                            />
                            <UniForm.InputGroupText class={""} text={"min"}/>
                            <UniForm.InputGroupHelpText text={"time.settings.time-after-auto-break-out.helper"}
                                                        class={"p-0 pe-1"} Store={""}/>
                        </UniForm.InputGroup>
                    </UniCol>
                </UniRow>

                {!userMode && (
                    <>
                        <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                            <UniCol xs={12}>
                                <UniChecks
                                    id="activateBreakOutWarningPopup"
                                    type="switch"
                                    label={IntlMessageValue("", "time.settings.activate-break-out-warning-popup", "")}
                                    name="activateBreakOutWarningPopup"
                                    checked={settings?.activateBreakOutWarningPopup}
                                    class="uni-switch ps-0 my-3"
                                    onChange={handleSwitchChange}
                                    info={{text: "time.settings.activate-break-out-warning-popup.helper"}}
                                />
                            </UniCol>
                            <UniCol xs={12}>
                                <p>{IntlMessage({
                                    locale: 'de',
                                    messageId: 'time.settings.break-out-warning-popup-content',
                                    Store: ''
                                })}</p>
                                <SunEditor
                                    key={'breakout-popup'}
                                    name={'breakOutWarningPopupContent'}
                                    setContents={settings.breakOutWarningPopupContent}
                                    setOptions={{
                                        buttonList: SunEditorConfig(),
                                        attributesWhitelist: {
                                            div: 'id',
                                            p: 'id',
                                            h: 'id'
                                        }
                                    }}
                                    height={'150px'}
                                    onChange={(value) => setEditorContent('breakOutWarningPopupContent', value)}
                                />
                            </UniCol>
                        </UniRow>

                        <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                            <UniCol xs={12}>
                                <UniChecks
                                    id="activateBreakOutWarningMail"
                                    type="switch"
                                    label={IntlMessageValue("", "time.settings.activate-break-out-warning-mail", "")}
                                    name="activateBreakOutWarningMail"
                                    checked={settings?.activateBreakOutWarningMail}
                                    class="uni-switch ps-0 mb-3"
                                    onChange={handleSwitchChange}
                                    info={{text: "time.settings.activate-break-out-warning-mail.helper"}}
                                />
                            </UniCol>
                            <UniCol xs={12}>
                                <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                                    <UniForm.InputGroupText class={""}
                                                            text={IntlMessageValue("", "time.settings.break-out-warning-mail-title", "")}/>
                                    <UniForm.FormControl
                                        type="text"
                                        name="breakOutWarningMailTitle"
                                        defaultValue={settings?.breakOutWarningMailTitle}
                                    />
                                    <UniForm.InputGroupHelpText
                                        text={"time.settings.break-out-warning-mail-title.helper"} class={"p-0 pe-1"}
                                        Store={""}/>
                                </UniForm.InputGroup>
                            </UniCol>
                            <UniCol xs={12}>
                                <p>{IntlMessage({
                                    locale: 'de',
                                    messageId: 'time.settings.break-out-warning-mail-content',
                                    Store: ''
                                })}</p>
                                <SunEditor
                                    key={'breakout-mail'}
                                    name={'breakOutWarningMailContent'}
                                    setContents={settings.breakOutWarningMailContent}
                                    setOptions={{
                                        buttonList: SunEditorConfig(),
                                        attributesWhitelist: {
                                            div: 'id',
                                            p: 'id',
                                            h: 'id'
                                        }
                                    }}
                                    height={'150px'}
                                    onChange={(value) => setEditorContent('breakOutWarningMailContent', value)}
                                />
                            </UniCol>
                        </UniRow>
                    </>
                )}

                <UniButton type="submit" class={"float-end"}>
                    {IntlMessage({locale: 'de', messageId: 'common.save', Store: ''})}
                </UniButton>
            </UniForm>
        </>
    );
};

export default AutoBreakOutSettings;
